'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authFetch } from '@/lib/api-client';
import { useTenant } from '@/hooks/use-tenant';

export interface TenantSetting {
  id: string;
  settingKey: string;
  settingName?: string;
  settingValue: unknown;
  settingType: 'string' | 'number' | 'boolean' | 'json' | 'array';
  category: string;
  description?: string;
  defaultValue?: unknown;
  allowedValues?: unknown[];
  validationRegex?: string;
  minValue?: number;
  maxValue?: number;
  isSensitive: boolean;
  // 'internal' settings are filtered out server-side and never reach the client;
  // it is present here only so the union matches the API contract.
  visibilityLevel: 'public' | 'member' | 'admin' | 'owner' | 'internal';
  campaignId?: string;
  eventId?: string;
  lastChangedBy?: string;
  createdAt: string;
  updatedAt: string;
}

type SettingScope = {
  campaignId?: string;
  eventId?: string;
};

export interface TenantSettingHistory {
  id: string;
  settingId: string;
  settingKey: string;
  previousValue?: unknown;
  newValue?: unknown;
  changedBy?: string;
  changedAt: string;
  changeReason?: string;
}

export const settingsKeys = {
  all: ['settings'] as const,
  lists: () => [...settingsKeys.all, 'list'] as const,
  list: (category?: string, campaignId?: string | null) =>
    [...settingsKeys.lists(), { category, campaignId }] as const,
  categories: () => [...settingsKeys.all, 'categories'] as const,
  detail: (key: string) => [...settingsKeys.all, 'detail', key] as const,
  history: (key: string) => [...settingsKeys.all, 'history', key] as const,
  byCategory: (category: string) => [...settingsKeys.all, 'category', category] as const,
};

/**
 * Fetch all settings, optionally filtered by category and/or campaign scope
 */
export function useSettings(category?: string, campaignId?: string | null) {
  const tenantId = useTenant();

  return useQuery({
    queryKey: settingsKeys.list(category, campaignId),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (category) params.category = category;
      if (campaignId) params.campaignId = campaignId;

      const data = await authFetch.get<TenantSetting[]>('/api/tenant/settings', {
        params,
        tenantId,
      });
      return data;
    },
    enabled: !!tenantId,
  });
}

/**
 * Fetch all setting categories
 */
export function useSettingCategories() {
  const tenantId = useTenant();

  return useQuery({
    queryKey: settingsKeys.categories(),
    queryFn: async () => {
      const data = await authFetch.get<string[]>('/api/tenant/settings/categories', {
        tenantId,
      });
      return data;
    },
    enabled: !!tenantId,
  });
}

/**
 * Fetch a specific setting by key
 */
export function useSetting(
  key: string,
  options?: { includeSensitive?: boolean; enabled?: boolean },
) {
  const tenantId = useTenant();

  return useQuery({
    queryKey: [...settingsKeys.detail(key), { includeSensitive: options?.includeSensitive }],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (options?.includeSensitive) params.includeSensitive = 'true';

      const data = await authFetch.get<TenantSetting>(
        `/api/tenant/settings/${encodeURIComponent(key)}`,
        { params, tenantId },
      );
      return data;
    },
    enabled: !!tenantId && !!key && options?.enabled !== false,
  });
}

export function useScopedSetting(
  key: string,
  scope?: SettingScope,
  options?: { includeSensitive?: boolean },
) {
  const tenantId = useTenant();

  return useQuery({
    queryKey: [
      ...settingsKeys.detail(key),
      {
        includeSensitive: options?.includeSensitive,
        campaignId: scope?.campaignId,
        eventId: scope?.eventId,
      },
    ],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (options?.includeSensitive) params.includeSensitive = 'true';
      if (scope?.campaignId) params.campaignId = scope.campaignId;
      if (scope?.eventId) params.eventId = scope.eventId;

      const data = await authFetch.get<TenantSetting>(
        `/api/tenant/settings/${encodeURIComponent(key)}`,
        { params, tenantId },
      );
      return data;
    },
    enabled: !!tenantId && !!key,
  });
}

/**
 * Fetch setting history
 */
export function useSettingHistory(key: string, limit?: number) {
  const tenantId = useTenant();

  return useQuery({
    queryKey: settingsKeys.history(key),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (limit) params.limit = limit.toString();

      const data = await authFetch.get<TenantSettingHistory[]>(
        `/api/tenant/settings/${encodeURIComponent(key)}/history`,
        { params, tenantId },
      );
      return data;
    },
    enabled: !!tenantId && !!key,
  });
}

export function useScopedSettingHistory(key: string, scope?: SettingScope, limit?: number) {
  const tenantId = useTenant();

  return useQuery({
    queryKey: [
      ...settingsKeys.history(key),
      { campaignId: scope?.campaignId, eventId: scope?.eventId },
    ],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (limit) params.limit = limit.toString();
      if (scope?.campaignId) params.campaignId = scope.campaignId;
      if (scope?.eventId) params.eventId = scope.eventId;

      const data = await authFetch.get<TenantSettingHistory[]>(
        `/api/tenant/settings/${encodeURIComponent(key)}/history`,
        { params, tenantId },
      );
      return data;
    },
    enabled: !!tenantId && !!key,
  });
}

/**
 * Update a setting value, optionally scoped to a campaign
 */
export function useUpdateSetting() {
  const queryClient = useQueryClient();
  const tenantId = useTenant();

  return useMutation({
    mutationFn: async ({
      key,
      value,
      changeReason,
      campaignId,
      eventId,
    }: {
      key: string;
      value: unknown;
      changeReason?: string;
      campaignId?: string;
      eventId?: string;
    }) => {
      const params: Record<string, string> = {};
      if (campaignId) params.campaignId = campaignId;
      if (eventId) params.eventId = eventId;

      return await authFetch.put<TenantSetting>(
        `/api/tenant/settings/${encodeURIComponent(key)}`,
        { value, changeReason },
        { tenantId, params },
      );
    },
    onSuccess: (_, { key }) => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: settingsKeys.detail(key) });
      void queryClient.invalidateQueries({ queryKey: settingsKeys.history(key) });
    },
  });
}

/**
 * Create a new setting (optionally campaign-scoped)
 */
export function useCreateSetting() {
  const queryClient = useQueryClient();
  const tenantId = useTenant();

  return useMutation({
    mutationFn: async (
      data: Omit<TenantSetting, 'id' | 'createdAt' | 'updatedAt' | 'lastChangedBy'>,
    ) => {
      return await authFetch.post<TenantSetting>('/api/tenant/settings', data, { tenantId });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: settingsKeys.categories() });
    },
  });
}

/**
 * Delete a setting (optionally campaign-scoped)
 */
export function useDeleteSetting() {
  const queryClient = useQueryClient();
  const tenantId = useTenant();

  return useMutation({
    mutationFn: async ({ key, campaignId }: { key: string; campaignId?: string }) => {
      const params: Record<string, string> = {};
      if (campaignId) params.campaignId = campaignId;

      await authFetch.delete(`/api/tenant/settings/${encodeURIComponent(key)}`, {
        tenantId,
        params,
      });
      return key;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: settingsKeys.categories() });
    },
  });
}

/**
 * Seed default settings
 */
export function useSeedSettings() {
  const queryClient = useQueryClient();
  const tenantId = useTenant();

  return useMutation({
    mutationFn: async () => {
      return await authFetch.post<{
        message: string;
        created: number;
        skipped: number;
        total: number;
      }>('/api/tenant/settings/seed', {}, { tenantId });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.all });
    },
  });
}

/**
 * Helper hook to get a specific setting value with type safety
 */
export function useSettingValue<T = unknown>(
  key: string,
  defaultValue?: T,
  options?: { includeSensitive?: boolean; enabled?: boolean },
): {
  value: T | undefined;
  isLoading: boolean;
  error: Error | null;
  update: (value: T, changeReason?: string) => Promise<void>;
} {
  const { data: setting, isLoading, error } = useSetting(key, options);
  const updateMutation = useUpdateSetting();

  const value = (setting?.settingValue as T | undefined) ?? defaultValue;

  const update = async (newValue: T, changeReason?: string) => {
    await updateMutation.mutateAsync({ key, value: newValue, changeReason });
  };

  return { value, isLoading, error, update };
}

/**
 * Hook specifically for the Mapbox access token
 * Note: This requests the actual sensitive value for use in API calls
 */
export function useMapboxToken() {
  return useSettingValue<string>('mapping.mapbox_access_token', undefined, {
    includeSensitive: true,
  });
}

/**
 * Hook specifically for the Google Places API key
 * Note: This requests the actual sensitive value for use in API calls.
 * Pass enabled: false to skip the fetch (e.g. when the key is provided via prop).
 */
export function useGooglePlacesApiKey(options?: { enabled?: boolean }) {
  return useSettingValue<string>('mapping.google_places_api_key', undefined, {
    includeSensitive: true,
    enabled: options?.enabled,
  });
}

/**
 * The tenant's platform/reporting currency code (`payment.default_currency`).
 * Falls back to USD while loading or when unset. Pair `code` with
 * `formatPlatformCurrency` to render money in the tenant's currency. (IMP-429)
 *
 * Prefer the `platformCurrencyCode` field returned on report/dashboard
 * responses when one is available — it is request-consistent. Use this hook for
 * standalone formatting where no response carries it.
 */
export function usePlatformCurrency(): {
  code: string;
  isLoading: boolean;
  error: Error | null;
  update: (value: string, changeReason?: string) => Promise<void>;
} {
  const { value, isLoading, error, update } = useSettingValue<string>(
    'payment.default_currency',
    'USD',
  );
  return { code: (value ?? 'USD').toUpperCase(), isLoading, error, update };
}
